import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { Btn, Spinner } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { Globe, Store, Mail, Eye } from "lucide-react";

const TYPE_META = {
  website: { label: "Website", icon: Globe, color: "bg-blue-50 text-blue-700" },
  service: { label: "Service", icon: Store, color: "bg-purple-50 text-purple-700" },
  contact: { label: "Contact", icon: Mail, color: "bg-slate-100 text-slate-600" },
};
const STATUSES = [["new", "New"], ["in_progress", "In progress"], ["on_hold", "On hold"], ["done", "Done"]];
const STATUS_COLOR = {
  new: "bg-amber-50 text-amber-700", in_progress: "bg-blue-50 text-blue-700",
  on_hold: "bg-slate-100 text-slate-600", done: "bg-emerald-50 text-emerald-700",
};

export default function AdminRequests({ onOpenWorkspace }) {
  const { data, loading, refetch } = useFetch("/admin/requests");
  const [typeF, setTypeF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("new");

  const items = (data?.items || []).filter((r) => (!typeF || r.type === typeF) && (!statusF || r.status === statusF));
  const open = (r) => { setSel(r); setNote(r.note || ""); setStatus(r.status || "new"); };
  const save = async () => {
    try {
      await api.post(`/admin/requests/${sel.type}/${sel.id}/status`, { status, note });
      toast.success("Request updated"); setSel(null); refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="py-10"><Spinner /></div>;
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        <select value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="requests-type-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
          <option value="">All types</option><option value="website">Website</option><option value="service">Service</option><option value="contact">Contact</option>
        </select>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="requests-status-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
          <option value="">All statuses</option>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="amden-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr>
            <th className="px-4 py-3 font-semibold">Type</th><th className="px-4 py-3 font-semibold">Subject</th>
            <th className="px-4 py-3 font-semibold hidden sm:table-cell">From</th><th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold text-right">When</th></tr></thead>
          <tbody>
            {items.map((r) => { const M = TYPE_META[r.type]; return (
              <tr key={`${r.type}-${r.id}`} onClick={() => open(r)} data-testid={`request-row-${r.id}`} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50">
                <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${M.color}`}><M.icon size={12} /> {M.label}</span></td>
                <td className="px-4 py-3 font-medium text-navy">{r.title}<span className="block text-xs text-slate-400 font-normal truncate max-w-[280px]">{r.summary}</span></td>
                <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{r.business_name || "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status]}`}>{STATUSES.find((s) => s[0] === r.status)?.[1] || "New"}</span></td>
                <td className="px-4 py-3 text-right text-slate-500">{fmtDate(r.created_at)}</td>
              </tr>); })}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No requests.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{sel?.title}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Request detail, status and internal note.</DialogDescription>
          {sel && (
            <div className="space-y-4 pt-1" data-testid="request-detail">
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {Object.entries(sel.detail || {}).map(([k, v]) => v ? (
                  <div key={k} className="flex gap-3 px-3 py-2 text-sm"><span className="w-28 shrink-0 text-slate-400">{k}</span><span className="text-navy break-words">{v}</span></div>
                ) : null)}
                {sel.business_name && <div className="flex gap-3 px-3 py-2 text-sm"><span className="w-28 shrink-0 text-slate-400">Client</span><span className="text-navy">{sel.business_name}</span></div>}
              </div>
              <div>
                <label className="text-xs text-slate-500">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="request-status-select" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white text-sm">
                  {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Internal note</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="request-note" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Notes for the team…" />
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {sel?.business_id && <Btn variant="secondary" onClick={() => onOpenWorkspace({ id: sel.business_id })} data-testid="request-open-workspace"><Eye size={15} /> Open workspace</Btn>}
            <Btn variant="secondary" onClick={() => setSel(null)}>Close</Btn>
            <Btn variant="accent" onClick={save} data-testid="request-save">Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
